import { AiFormFieldProcessing } from './ai-form.types';

export function formatAiFormDate(date: Date): string {
    return [String(date.getFullYear()).padStart(4, '0'), String(date.getMonth() + 1).padStart(2, '0'),
        String(date.getDate()).padStart(2, '0')].join('-');
}

export const dateValue: AiFormFieldProcessing = {
    read: value => {
        if (value instanceof Date) return Number.isFinite(value.getTime()) ? formatAiFormDate(value) : null;
        if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}(?:T|$)/.test(value)) return value.slice(0, 10);
        return value;
    },
    write: value => value ? new Date(`${value}T00:00:00`) : null,
};

export const numberValue: AiFormFieldProcessing = {
    read: value => {
        if (value == null || value === '') return null;
        const number = typeof value === 'number' ? value : Number(String(value).replace(/\s/g, '').replace(',', '.'));
        return Number.isFinite(number) ? number : value;
    },
};

export function templateBooleanValue(options: { id: any; name: string }[], asName: boolean): AiFormFieldProcessing {
    return {
        read: value => {
            value = value?.id ?? value;
            if (typeof value === 'boolean' || value == null) return value;
            const option = options.find(item => String(item.id) === String(value) || item.name === value);
            return option ? String(option.id) === 'true' : null;
        },
        write: value => {
            if (value == null || !asName) return value;
            const option = options.find(item => String(item.id) === String(value));
            if (!option) throw new Error('Недоступен вариант логического поля');
            return option.name;
        },
    };
}

export function templateReferenceValue(options: { id: any; name: string }[], asName: boolean): AiFormFieldProcessing {
    const reference = referenceValue();
    return {
        ...reference,
        read: value => {
            if (value == null || value === '') return null;
            if (typeof value === 'object') return value.id == null ? null : String(value.id);
            const matches = options.filter(item => asName ? item.name === value : String(item.id) === String(value));
            return matches.length === 1 ? String(matches[0].id) : null;
        },
        write: async (value, state, currentValue, context) => {
            const selected: any = await reference.write(value, state, currentValue, context);
            if (!selected || !asName) return selected;
            if (options.filter(item => item.name === selected.name).length !== 1) {
                throw new Error(`Вариант «${selected.name}» поля ${context.field.key} неоднозначен в таблице`);
            }
            return selected.name;
        },
    };
}

export const idValue: AiFormFieldProcessing = {
    read: value => value?.id ?? value ?? null,
};

export function optionValue(optionsKey: string): AiFormFieldProcessing {
    return {
        read: value => value?.id == null ? null : String(value.id),
        write: (value, state) => {
            const options = state.options?.[optionsKey] ?? [];
            const option = value ? options.find(item => String(item.id) === value)
                : options.find(item => item.isDefault);
            if (value && !option) throw new Error(`Неизвестный ID в справочнике ${optionsKey}`);
            return option ?? null;
        },
    };
}

export function referenceValue(
    multiple = false, option = (item: any) => ({ id: String(item.id), label: String(item.name) }),
): AiFormFieldProcessing {
    return {
        option,
        read: value => multiple ? (value ?? []).map(item => ({ id: option(item).id })) : value ? option(value).id : null,
        write: async (value, state, _currentValue, { field, signal }) => {
            const ids: string[] = multiple ? value.map(item => item.id) : value == null ? [] : [value];
            if (!ids.length) return multiple ? [] : null;
            if (ids.some(id => typeof id !== 'string' || !id) || new Set(ids).size !== ids.length) {
                throw new Error(`Некорректные ID поля ${field.key}`);
            }
            const result = await field.caller!({ ids }, { state, signal });
            const selected = ids.map(id => {
                const item = result.items.find(item => option(item).id === id);
                if (!item) throw new Error(`Значение ${id} поля ${field.key} недоступно`);
                return item;
            });
            return multiple ? selected : selected[0];
        },
    };
}

export function arrayRows(valueKey: string, firstItemFlag?: string): AiFormFieldProcessing {
    return {
        read: value => (value ?? []).filter(item => item[valueKey] != null && item[valueKey] !== ''),
        write: (value, _state, currentValue) => value.map((item, index) => ({
            // shortcut: ID строк сохраняются по индексу, при поддержке перестановки нужен стабильный ID строки.
            ...(currentValue?.[index] ?? { id: null }), ...item,
            ...(firstItemFlag ? { [firstItemFlag]: index === 0 } : {}),
        })),
    };
}
